import React from "react"
import { Button, Form, Input, Select } from "antd"

import { useProjectStore } from "stores/projectStore"

import { useUpdateProjectMutation } from "features/Projects/api/useProjectsMutations"

import { UpdateProjectBody } from "types/project.types"

// Define the props interface
interface GeneralInformationTabProps {
  refreshProject: () => void
}

// Define the form values interface
interface FormValues {
  Name: string
  Goal: string
  Status: string
  Type: string
  Designlanguage: string
  languages: string[]
}

/**
 * GeneralInformationTab component - Renders a form for editing general information of a project.
 *
 * This component displays a form populated with the current project's information, allowing the user
 * to edit and save changes. It utilizes Ant Design's Form component for UI consistency and validation.
 * The form includes fields for project name, goal, status, type, design language, and programming languages.
 * Upon submission, it calls the `updateUserProject` function with the updated project details.
 *
 * @component
 * @param props - The component props.
 * @returns The GeneralInformationTab component, rendering the editable form.
 */
const GeneralInformationTab: React.FC<GeneralInformationTabProps> = (props) => {
  const { refreshProject } = props
  const [form] = Form.useForm<FormValues>()
  const updateProjectMutation = useUpdateProjectMutation()
  const project = useProjectStore((state) => state.project)

  /**
   * Handles form submission to save changes.
   *
   * This function is called when the form is submitted. It constructs a request body from the form values
   * and calls the `updateUserProject` API function. If the update is successful, it shows a success notification
   * and refreshes the project details. If an error occurs, it shows an error notification.
   *
   * @async
   * @function
   * @param formValues - The values of the form fields.
   */
  const saveChanges = async (formValues: FormValues): Promise<void> => {
    const body: UpdateProjectBody = {
      id: project.id,
      name: formValues.Name,
      goal: formValues.Goal,
      status: formValues.Status,
      type: formValues.Type,
      designLanguage: formValues.Designlanguage,
      languages: formValues.languages,
    }

    try {
      await updateProjectMutation.mutateAsync(body)
      refreshProject()
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error updating project:", error)
    }
  }

  /** A small uppercase heading over a group of fields. */
  const groupHeading = (text: string) => (
    // mt-0: editor.css gives every h3 a 2.5rem top margin.
    <h3 className="mt-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {text}
    </h3>
  )

  return (
    <div className="update-project-form h-full overflow-auto rounded-xl bg-white">
      <Form
        form={form}
        name="update-project-form"
        onFinish={saveChanges}
        layout="vertical"
        requiredMark={false}
        className="flex min-h-full flex-col"
        initialValues={{
          languages: project.Languages,
          Name: project.Name,
          Designlanguage: project.Designlanguage,
          Goal: project.Goal,
          Status: project.Status,
          Type: project.Type.toUpperCase(),
        }}
      >
        {/* Heading row with the save, so it is never a scroll away */}
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-gray-100 bg-white px-6 py-3">
          <div className="min-w-0">
            <h2 className="m-0 text-base font-semibold text-gray-900">
              General
            </h2>
            <p className="m-0 text-xs text-gray-500">
              The project's name, goal, languages and status
            </p>
          </div>
          <Button
            type="primary"
            htmlType="submit"
            disabled={updateProjectMutation.isPending}
            className="ml-auto"
          >
            {updateProjectMutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <div className="w-full max-w-3xl space-y-4 px-6 py-4">
          <section>
            {groupHeading("Project")}
            <Form.Item
              label="Project name"
              validateTrigger="onSubmit"
              name="Name"
              rules={[
                {
                  required: true,
                  message: "Please Enter a Name",
                },
              ]}
            >
              <Input placeholder="Enter project name" />
            </Form.Item>
            <Form.Item
              label="Project goal"
              validateTrigger="onSubmit"
              name="Goal"
              rules={[
                {
                  required: false,
                  message: "Please enter a project goal",
                },
              ]}
            >
              <Input.TextArea
                placeholder="Describe the main objectives and goals of this project..."
                autoSize={{ minRows: 3, maxRows: 8 }}
              />
            </Form.Item>
          </section>

          <section>
            {groupHeading("Languages")}
            <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
              <Form.Item
                label="Design language"
                validateTrigger="onSubmit"
                name="Designlanguage"
                rules={[
                  {
                    required: true,
                    message: "Please Enter a Design Language",
                  },
                ]}
              >
                <Input placeholder="e.g., English, Spanish" />
              </Form.Item>
              <Form.Item name="languages" label="Languages">
                <Select
                  mode="tags"
                  placeholder="Select or add languages"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
          </section>

          <section>
            {groupHeading("Details")}
            <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
              <Form.Item name="Status" label="Status">
                <Input placeholder="e.g., In Progress, Completed" />
              </Form.Item>
              <Form.Item label="Type" name="Type">
                <Input placeholder="Project type" disabled />
              </Form.Item>
            </div>
          </section>
        </div>
      </Form>
    </div>
  )
}

export default GeneralInformationTab
